import React, { useState } from 'react';
import BentoCard from '../components/BentoCard';
import { Link } from 'react-router-dom';
import { splitEvents, countdownLabel } from '../configs/eventInfo';

// Image Imports
import skateDay2025 from '../images/events/skateDay2025.png';
import sponsorshipForm from '../images/events/sponsorshipForm.png';
import familyFunDay from '../images/events/FunDay2026.jpg';
import flyer2025 from '../images/homepagePics/flyer2025.jpg';
import fourthFunday from '../images/homepagePics/fourthFundayFlyer.png';
import newsletterAug24 from '../images/homepagePics/NewsletterAugust2024.png';
import newsletterMay24 from '../images/homepagePics/newsletterMay2024.jpg';
import october2022 from '../images/homepagePics/october2022news.JPG';
import pressRelease from '../images/homepagePics/PositiveSomebodyPressRelease.jpg';
import pressReleaseMar3 from '../images/homepagePics/pressReleaseMarch23-2.JPG';
import pressReleaseMar from '../images/homepagePics/pressReleaseMarch23.JPG';
import programsNews from '../images/homepagePics/programsAndNews.JPG';
import funDay2024 from '../images/events/funDay2024.jpg';
import funDay2 from '../images/events/2ndFunDay.jpg';
import funDay22 from '../images/events/funDayImage22.jpg';
import skateDayPast from '../images/events/skateDay.jpg';
import crowdShot from '../images/events/IMG-4199.JPG';
import funDay2025 from '../images/homepagePics/flyer2025.jpg';

const Events = () => {
    // State to manage the full-screen image modal
    const [selectedImage, setSelectedImage] = useState(null);

    // Helper function to close modal
    const closeModal = () => setSelectedImage(null);

    // Events, their Upcoming/Past split and the countdown label all come from
    // src/configs/eventInfo.js, which the homepage reads from too.
    const { upcoming: upcomingEvents, past: pastEvents } = splitEvents();

    // Calendar card - merged into the first upcoming event's row.
    const renderCalendarCard = () => (
        <BentoCard title="Full Schedule" className="p-0 overflow-hidden min-h-[500px] relative bg-white border-2 border-primary-navy">
            <div className="absolute inset-x-0 bottom-0 top-16 w-full">
                <iframe
                    src="https://calendar.google.com/calendar/embed?src=4ab281c3475bdb1d1437e1757b35cc7279b76f90b01402e7a826af10cfc9244a%40group.calendar.google.com&ctz=America%2FDetroit&mode=WEEK"
                    style={{ border: 0 }}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Positive Somebody Calendar"
                ></iframe>
            </div>
        </BentoCard>
    );

    // Renders one event as a text card + image card row (optionally with the
    // calendar as a third column). variant controls the badge label/color and
    // the CTA button text/link.
    const renderEventCard = (event, index, variant, includeCalendar = false) => {
        const isUpcoming = variant === "upcoming";
        const badgeText = isUpcoming ? "Upcoming Event" : "Recent Highlight";
        const badgeColor = isUpcoming ? "bg-accent-orange" : "bg-primary-forest";
        const ctaText = isUpcoming ? "Become a Sponsor" : "View Past Events";
        const ctaHref = isUpcoming ? "#sponsor" : "#gallery";
        const textColorClass = index % 2 === 0 ? "text-blue-100" : "text-green-100";
        const subTextColorClass = index % 2 === 0 ? "text-blue-200" : "text-green-200";

        // Spotlight treatment for the next big event (see EVENT CONFIGURATION).
        const isFeatured = isUpcoming && event.featured;
        const countdown = isFeatured ? countdownLabel(event.date) : null;
        const badgePill = "inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm";

        const row = (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Text Card */}
                <BentoCard className="flex flex-col justify-between min-h-[500px] !bg-primary-navy text-white !border-primary-navy">
                    <div>
                        <div className="mb-8">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <span className={`${badgePill} text-white ${isFeatured ? "bg-accent-orange" : badgeColor}`}>
                                    {isFeatured && event.ticketUrl ? "Tickets On Sale Now" : badgeText}
                                </span>
                                {countdown && (
                                    <span className={`${badgePill} bg-accent-yellow text-primary-navy`}>
                                        {countdown}
                                    </span>
                                )}
                            </div>
                            <h3 className="text-3xl font-bold font-heading leading-tight text-white">{event.title}</h3>
                        </div>
                        <div className={`space-y-6 ${textColorClass} mb-8`}>
                            <p className="leading-relaxed text-lg font-medium">
                                {event.description}
                            </p>
                            <ul className="space-y-4">
                                <li className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl">📅</div>
                                    <div>
                                        <p className="font-bold text-white text-lg">{event.displayDate}</p>
                                        <p className={`text-sm ${subTextColorClass}`}>{event.displayTime}</p>
                                    </div>
                                </li>
                                <li className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl">📍</div>
                                    <div>
                                        <p className="font-bold text-white text-lg">{event.location}</p>
                                        {event.city && <p className={`text-sm ${subTextColorClass}`}>{event.city}</p>}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    {isUpcoming && event.ticketUrl ? (
                        <div className="space-y-3">
                            <a
                                href={event.ticketUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full px-6 py-3 rounded-full text-center bg-accent-yellow text-primary-navy font-heading font-bold text-sm uppercase tracking-wide border-2 border-primary-navy shadow-[4px_4px_0px_#020617] hover:bg-yellow-300 hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#020617] transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                            >
                                Get Tickets ↗
                            </a>
                            {event.ticketNote && (
                                <p className={`text-xs text-center ${subTextColorClass}`}>{event.ticketNote}</p>
                            )}
                        </div>
                    ) : (
                        <a href={ctaHref} className="w-full text-center bg-accent-yellow text-primary-navy px-6 py-4 rounded-full font-bold hover:bg-white transition-colors text-lg shadow-[4px_4px_0px_#000000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none border-2 border-black">
                            {ctaText}
                        </a>
                    )}
                </BentoCard>

                {/* Image Card */}
                <BentoCard className={`p-4 border-4 border-primary-navy flex items-center justify-center bg-white min-h-[500px] ${includeCalendar ? "" : "lg:col-span-2"}`}>
                    <div
                        className="relative group cursor-pointer w-full h-full flex items-center justify-center overflow-hidden rounded-lg"
                        onClick={() => setSelectedImage(event.image)}
                    >
                        <img src={event.image} alt={event.imageAlt} className="w-full h-full object-contain shadow-sm group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 bg-white/90 p-2 rounded-full shadow-lg transform scale-90 group-hover:scale-100 transition-all text-sm font-bold text-primary-navy">
                                🔍 View
                            </span>
                        </div>
                    </div>
                </BentoCard>

                {/* Calendar Card - only on the first upcoming event's row */}
                {includeCalendar && renderCalendarCard()}
            </div>
        );

        if (!isFeatured) return <div key={event.id}>{row}</div>;

        return (
            <div key={event.id} className="rounded-bento border-4 border-accent-orange bg-accent-orange/10 p-4 sm:p-6 shadow-bento">
                {row}
            </div>
        );
    };

    // Countdown shown next to the "Upcoming Events" heading, when the soonest
    // upcoming event is flagged as featured.
    const featuredCountdown = upcomingEvents[0]?.featured
        ? countdownLabel(upcomingEvents[0].date)
        : null;

    // Image Array for Wall of Impact
    const impactImages = [
        familyFunDay,
        funDay2025,
        funDay2024,
        funDay2,
        funDay22,
        skateDayPast,
        crowdShot,
        flyer2025,
        fourthFunday,
        newsletterAug24,
        newsletterMay24,
        october2022,
        skateDay2025,
        pressRelease,
        pressReleaseMar,
        pressReleaseMar3,
        programsNews,
    ];

    return (
        <main className="max-w-7xl mx-auto p-4 lg:p-8 space-y-12">

            {/* --- IMAGE MODAL (LIGHTBOX) --- */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
                    onClick={closeModal}
                >
                    <button
                        onClick={closeModal}
                        className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <img
                        src={selectedImage}
                        alt="Full view"
                        className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl cursor-default"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            {/* HEADER */}
            <div className="text-center max-w-3xl mx-auto mb-8">
                <h1 className="text-5xl font-heading font-bold text-primary-navy mb-4">Events & Impact</h1>
                <p className="text-xl font-bold text-gray-700">
                    From skate days to fun days to educational outreach, we bring the community together to celebrate and learn about mental wellness.
                </p>
            </div>

            {/* UPCOMING EVENTS & SCHEDULE */}
            <div id="upcoming" className="scroll-mt-24">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
                    <div className="h-1 w-12 bg-accent-orange rounded-full"></div>
                    <h2 className="text-3xl font-heading font-bold text-primary-navy">Upcoming Events & Schedule</h2>
                    {featuredCountdown && (
                        <span className="text-accent-orange font-bold uppercase tracking-wide text-sm">
                            Next up · {featuredCountdown}
                        </span>
                    )}
                </div>

                <div className="space-y-12">
                    {upcomingEvents.length > 0 ? (
                        upcomingEvents.map((event, index) =>
                            renderEventCard(event, index, "upcoming", index === 0)
                        )
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 flex items-center justify-center text-center p-8 min-h-[500px] text-xl font-medium text-gray-500 bg-white rounded-2xl border-2 border-primary-navy/10">
                                No upcoming events scheduled — check the calendar for updates!
                            </div>
                            {renderCalendarCard()}
                        </div>
                    )}
                </div>
            </div>

            {/* PAST EVENTS */}
            <div className="scroll-mt-24">
                <div className="flex items-center gap-4 mb-6">
                    <div className="h-1 w-12 bg-accent-orange rounded-full"></div>
                    <h2 className="text-3xl font-heading font-bold text-primary-navy">Past Events</h2>
                </div>

                <div className="space-y-12">
                    {pastEvents.map((event, index) => renderEventCard(event, index, "past"))}
                </div>
            </div>

            {/* SECTION 2: WALL OF IMPACT */}
            <div id="gallery">
                <div className="flex items-center gap-4 mb-6">
                    <div className="h-1 w-12 bg-teal-500 rounded-full"></div>
                    <h2 className="text-3xl font-heading font-bold text-primary-navy">Wall of Impact</h2>
                </div>

                {/* MASONRY GRID for Documents & Flyers */}
                <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                    {impactImages.map((img, index) => (
                        <div
                            key={index}
                            className="break-inside-avoid rounded-2xl overflow-hidden shadow-lg group relative border-2 border-primary-navy/10 cursor-pointer bg-white"
                            onClick={() => setSelectedImage(img)}
                        >
                            <img
                                src={img}
                                alt={`Impact Gallery Item ${index + 1}`}
                                className="w-full h-auto block hover:scale-105 transition-transform duration-700"
                            />
                            {/* Hover Overlay Hint */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                <span className="opacity-0 group-hover:opacity-100 bg-white/90 p-2 rounded-full shadow-lg transform scale-90 group-hover:scale-100 transition-all text-sm font-bold text-primary-navy">
                                    🔍 View
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* SECTION 3: SPONSORSHIP */}
            <div id="sponsor">
                <BentoCard className="bg-blue-50/50 backdrop-blur-sm border-2 border-primary-navy p-8 lg:p-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

                        {/* Text Content */}
                        <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
                            <h3 className="text-3xl font-bold text-primary-navy">Sponsor an Event</h3>
                            <p className="text-xl text-primary-navy/80 font-medium leading-relaxed">
                                Our events are free for families thanks to generous local sponsors.
                                Your business can make an impact. Click the flyer to see sponsorship levels.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                                <Link to="/donate" className="flex items-center justify-center gap-2 bg-primary-navy text-white px-8 py-4 rounded-full font-bold hover:bg-primary-forest transition-colors shadow-[4px_4px_0px_#020617] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                                    <span>❤</span> Donate Now
                                </Link>
                                <a
                                    href="mailto:beapositivesomebody@gmail.com?subject=Sponsorship%20Inquiry&body=Hello%2C%0D%0A%0D%0AI%20am%20interested%20in%20sponsorship%20opportunities%20for%20your%20upcoming%20events.%20Please%20send%20me%20information.%0D%0A%0D%0ACompany%20Name%3A%20%5BName%5D%0D%0A%0D%0AThank%20you%2C"
                                    className="flex items-center justify-center gap-2 border-2 border-primary-navy text-primary-navy px-8 py-4 rounded-full font-bold hover:bg-white transition-colors shadow-[4px_4px_0px_#020617] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none bg-white"
                                >
                                    Contact Us
                                </a>
                            </div>
                        </div>

                        {/* Flyer Image */}
                        <div className="lg:col-span-5 w-full flex justify-center lg:justify-end">
                            <div
                                className="w-full max-w-sm bg-white p-2 rounded-xl border border-gray-200 shadow-xl cursor-pointer group transition-all hover:-translate-y-1 hover:shadow-2xl relative"
                                onClick={() => setSelectedImage(sponsorshipForm)}
                            >
                                <div className="relative overflow-hidden rounded-lg">
                                    <img src={sponsorshipForm} alt="Sponsorship Form Preview" className="w-full h-auto" />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                        <span className="opacity-0 group-hover:opacity-100 bg-white px-4 py-2 rounded-full shadow-lg text-sm font-bold text-primary-navy transform translate-y-2 group-hover:translate-y-0 transition-all">
                                            🔍 Click to Enlarge
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </BentoCard>
            </div>

        </main>
    );
};

export default Events;